import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import { CalendarDays, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Tournament, tournamentsQueryOptions } from "@/api/tournaments";
import { useSelectedTeam } from "@/context/teamContext";
import { SelectMatchesDialogTable } from "./SelectMatchesDialogTable";
import { useSelectedMatch } from "@/context/matchContext";
import dayjs from "dayjs";
import { cn, dateFormat } from "@/lib/utils";

interface SelectMatchesDesktopDialogProps {
    selectedMatchIds?: number[];
    onMatchSelect?: (matchIds: number[]) => void;
    useExternalState?: boolean;
}

export default function SelectMatchesDesktopDialog({
    selectedMatchIds = [],
    onMatchSelect,
    useExternalState = false,
}: SelectMatchesDesktopDialogProps = {}) {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<"browse" | "selected">("browse");
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [tempSelectedMatchIds, setTempSelectedMatchIds] = useState<number[]>([]);

    const contextData = useSelectedMatch();
    const { matchIds: contextMatchIds, setMatchIds, setTournamentName } = contextData || {};
    const { teamId } = useSelectedTeam();

    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));

    const currentMatchIds = useMemo(
        () => (useExternalState ? selectedMatchIds : contextMatchIds || []),
        [useExternalState, selectedMatchIds, contextMatchIds]
    );

    const allMatches = useMemo(
        () =>
            tournaments?.flatMap((tournament) =>
                tournament.matches.map((match) => ({
                    ...match,
                    tournamentName: tournament.name,
                    tournamentId: tournament.id,
                }))
            ) || [],
        [tournaments]
    );

    const handleTournamentSelect = (tournamentName: string) => {
        const tournament = tournaments?.find((t) => t.name === tournamentName);
        setSelectedTournament(tournament ?? null);
    };

    const handleMatchesSelect = (newMatchIds: number[]) => {
        const currentTournamentMatches = selectedTournament?.matches.map((m) => m.id) || [];

        const matchesFromOtherTournaments = tempSelectedMatchIds.filter((id) => !currentTournamentMatches.includes(id));

        const updatedSelection = [...matchesFromOtherTournaments, ...newMatchIds];

        setTempSelectedMatchIds(updatedSelection);
    };

    const handleRemoveMatch = (matchIdToRemove: number) => {
        setTempSelectedMatchIds((prev) => prev.filter((id) => id !== matchIdToRemove));
    };

    const handleMatchSelect = () => {
        if (useExternalState) {
            onMatchSelect?.(tempSelectedMatchIds);
        } else {
            if (setMatchIds) {
                setMatchIds(tempSelectedMatchIds);
                if (tempSelectedMatchIds.length > 0) {
                    const firstMatch = allMatches.find((m) => tempSelectedMatchIds.includes(m.id));
                    if (firstMatch && setTournamentName) {
                        setTournamentName(firstMatch.tournamentName);
                    }
                }
            }
        }
        setOpen(false);
    };

    const handleClearSelection = () => {
        if (useExternalState) {
            onMatchSelect?.([]);
        } else {
            setMatchIds?.([]);
        }
        setOpen(false);
    };

    useEffect(() => {
        if (!open || !tournaments) return;

        setTempSelectedMatchIds((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(currentMatchIds)) {
                return [...currentMatchIds];
            }
            return prev;
        });
        setView("browse");

        if (currentMatchIds.length > 0) {
            const firstMatch = allMatches.find((m) => currentMatchIds.includes(m.id));
            if (firstMatch) {
                const tournament = tournaments.find((t) => t.matches.some((m) => m.id === firstMatch.id));
                setSelectedTournament((prev) => {
                    if (prev?.id !== tournament?.id) {
                        return tournament || null;
                    }
                    return prev;
                });
            }
        } else {
            const newestTournament = [...tournaments].sort((a, b) => b.id - a.id)[0];
            setSelectedTournament((prev) => {
                if (prev === null && newestTournament) {
                    return newestTournament;
                }
                return prev;
            });
        }
    }, [open, currentMatchIds, tournaments, allMatches]);

    useEffect(() => {
        if (!open) {
            setSelectedTournament(null);
        }
    }, [open]);

    const selectedMatches = useMemo(
        () => allMatches.filter((match) => tempSelectedMatchIds.includes(match.id)),
        [allMatches, tempSelectedMatchIds]
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outlined">
                    <CalendarDays size={16} className="mr-1" />
                    Select match{currentMatchIds.length > 1 ? "es" : ""}
                </Button>
            </DialogTrigger>
            <DialogContent className="p-0 w-fit max-h-[90vh] flex flex-col rounded-md" showCloseButton={false}>
                <div className="px-6 py-4 border-b border-outline-variant/20 bg-surface-container-low shrink-0">
                    <div className="flex items-center justify-between mb-3">
                        <DialogTitle className="text-xl font-headline font-bold text-on-surface">
                            Select Matches
                        </DialogTitle>
                        <DialogClose className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer">
                            <X size={18} />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </div>
                    <div className="flex gap-1 bg-surface-container rounded-lg p-1">
                        <button
                            onClick={() => setView("browse")}
                            className={cn(
                                "flex-1 py-1.5 text-xs font-headline font-bold uppercase tracking-wide rounded-md transition-colors cursor-pointer",
                                view === "browse"
                                    ? "bg-surface-container-lowest text-on-surface shadow-sm"
                                    : "text-on-surface-variant hover:text-on-surface"
                            )}
                        >
                            Browse
                        </button>
                        <button
                            onClick={() => setView("selected")}
                            className={cn(
                                "flex-1 py-1.5 text-xs font-headline font-bold uppercase tracking-wide rounded-md transition-colors flex items-center justify-center gap-1.5 cursor-pointer",
                                view === "selected"
                                    ? "bg-surface-container-lowest text-on-surface shadow-sm"
                                    : "text-on-surface-variant hover:text-on-surface"
                            )}
                        >
                            Selected
                            {tempSelectedMatchIds.length > 0 && (
                                <span
                                    className={cn(
                                        "text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none",
                                        view === "selected"
                                            ? "bg-primary text-white"
                                            : "bg-on-surface-variant/20 text-on-surface-variant"
                                    )}
                                >
                                    {tempSelectedMatchIds.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {view === "browse" && (
                        <>
                            <div className="px-6 py-3 border-b border-outline-variant/20 shrink-0">
                                <Select value={selectedTournament?.name || ""} onValueChange={handleTournamentSelect}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select a tournament" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {tournaments && tournaments.length > 0 ? (
                                            tournaments.map((tournament: Tournament) => (
                                                <SelectItem key={tournament.name} value={tournament.name}>
                                                    <div className="flex items-baseline gap-2">
                                                        <span>{tournament.name}</span>
                                                        <span className="text-sm text-on-surface-variant">
                                                            {tournament.id}
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-on-surface-variant text-sm">
                                                No tournament data available
                                            </div>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="px-6 py-4">
                                <SelectMatchesDialogTable
                                    matches={selectedTournament?.matches ?? []}
                                    tournamentSelected={!!selectedTournament}
                                    defaultSelectedMatchIds={
                                        selectedTournament
                                            ? tempSelectedMatchIds.filter((id) =>
                                                  selectedTournament.matches.some((m) => m.id === id)
                                              )
                                            : []
                                    }
                                    onSelectMatches={handleMatchesSelect}
                                />
                            </div>
                        </>
                    )}

                    {view === "selected" && (
                        <div className="w-[648px] min-h-[484px] px-6 py-4">
                            {tempSelectedMatchIds.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 gap-2 text-on-surface-variant">
                                    <p className="text-sm">No matches selected yet.</p>
                                    <button
                                        onClick={() => setView("browse")}
                                        className="text-xs text-primary hover:underline cursor-pointer"
                                    >
                                        Switch to Browse to add matches
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {selectedMatches.map((match) => (
                                        <div
                                            key={match.id}
                                            className="flex items-center gap-4 px-3 py-2.5 rounded-md bg-surface-container"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-on-surface">
                                                    {match.homeTeam.shortName} vs {match.awayTeam.shortName}
                                                </p>
                                                <p className="text-xs text-on-surface-variant mt-0.5">
                                                    {match.tournamentName} · {dayjs(match.date).format(dateFormat)}
                                                </p>
                                            </div>
                                            <span className="text-sm font-mono text-on-surface-variant shrink-0">
                                                {match.homeGoals} – {match.awayGoals}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveMatch(match.id)}
                                                className="text-on-surface-variant hover:text-general-red transition-colors shrink-0 cursor-pointer"
                                                aria-label="Remove match"
                                            >
                                                <X size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 py-4 px-6 bg-surface-container-low border-t border-outline-variant/20 shrink-0">
                    <button
                        onClick={() => setOpen(false)}
                        className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-on-surface-variant rounded-md transition-colors cursor-pointer font-headline font-bold uppercase tracking-wider text-xs"
                    >
                        Cancel
                    </button>
                    {useExternalState && (
                        <Button
                            variant="outlined"
                            onClick={handleClearSelection}
                            disabled={currentMatchIds.length === 0}
                        >
                            Clear Selection
                        </Button>
                    )}
                    <Button variant="default" disabled={tempSelectedMatchIds.length === 0} onClick={handleMatchSelect}>
                        Select Match{tempSelectedMatchIds.length > 1 ? "es" : ""}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
